import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { Eye, EyeOff, Loader2, Lock, UserRound } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'

import { confirmLoginOtp, loginRequest } from '@/api/auth'
import { AnimatedLoginShell } from '@/components/auth/AnimatedLoginShell'
import { AuthCaptchaNotice } from '@/components/auth/AuthCaptchaNotice'
import { brandAssetsFromBranding } from '@/components/brand/AdminBrandLogo'
import { RecaptchaWidget } from '@/components/auth/RecaptchaWidget'
import { useAuthCaptcha } from '@/hooks/useAuthCaptcha'
import { useAdminTheme } from '@/hooks/useAdminLayoutPreferences'
import { tenantDisplayName, useTenantBranding } from '@/hooks/useTenantBranding'
import { hasBackofficeAccess } from '@/lib/auth-access'
import { isAuthCaptchaError } from '@/lib/auth-captcha'
import { useT } from '@/lib/i18n/AdminI18nProvider'
import { getRequestErrorMessage } from '@/lib/http-errors'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth.store'
import { AdminLanguageSwitcher } from '@/components/layout/AdminLanguageSwitcher'

const _baseSchema = z.object({ login: z.string().min(1), password: z.string().min(1) })
type FormValues = z.infer<typeof _baseSchema>

export function LoginPage() {
  const t = useT()
  const navigate = useNavigate()
  const location = useLocation()
  const setAuth = useAuthStore((s) => s.setAuth)
  const [showPassword, setShowPassword] = useState(false)
  const [challenge, setChallenge] = useState<string | null>(null)
  const [otpCode, setOtpCode] = useState('')
  const { mode } = useAdminTheme()
  const { data: branding } = useTenantBranding()
  const storeName = tenantDisplayName(branding)
  const brandAssets = branding ? brandAssetsFromBranding(branding) : {}

  const {
    captchaRequired,
    showCaptcha,
    siteKey,
    recaptchaToken,
    setRecaptchaToken,
    refreshStatus,
    markCaptchaRequired,
    clearCaptchaToken,
  } = useAuthCaptcha()

  const from =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/'

  const schema = useMemo(
    () => z.object({
      login: z.string().min(1, t('auth.login.emailRequired')),
      password: z.string().min(1, t('auth.login.passwordRequired')),
    }),
    [t],
  )

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { login: '', password: '' },
  })

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      loginRequest(
        values.login.trim(),
        values.password,
        captchaRequired ? recaptchaToken : null,
      ),
    onSuccess: (res) => {
      if (res.success && res.data?.requiresOtp && res.data.challenge) {
        setChallenge(res.data.challenge)
        toast.success(res.data.message || 'Telefona doğrulama kodu gönderildi.')
        return
      }
      if (!res.success || !res.data?.token || !res.data.user) {
        toast.error(t('auth.login.unexpectedResponse'))
        return
      }
      if (!hasBackofficeAccess(res.data.user)) {
        toast.error(t('auth.login.invalidCredentials'))
        return
      }
      setAuth(res.data.token!, res.data.user!)
      toast.success(t('auth.login.welcome'))
      navigate(from, { replace: true })
    },
    onError: (err: unknown) => {
      if (isAuthCaptchaError(err)) {
        markCaptchaRequired(err.recaptcha)
        clearCaptchaToken()
        toast.error(err.message)
        void refreshStatus()
        return
      }
      toast.error(getRequestErrorMessage(err, t('auth.login.failed')))
      void refreshStatus()
    },
  })

  const otpMutation = useMutation({
    mutationFn: () => confirmLoginOtp(challenge ?? '', otpCode),
    onSuccess: (res) => {
      if (!res.success || !res.data?.token || !res.data.user) {
        toast.error(t('auth.login.unexpectedResponse'))
        return
      }
      if (!hasBackofficeAccess(res.data.user)) {
        toast.error(t('auth.login.invalidCredentials'))
        return
      }
      setAuth(res.data.token, res.data.user)
      toast.success(t('auth.login.welcome'))
      navigate(from, { replace: true })
    },
    onError: (err: unknown) => {
      toast.error(getRequestErrorMessage(err, 'Doğrulama kodu geçersiz.'))
    },
  })

  return (
    <AnimatedLoginShell
      storeName={storeName}
      isDark={mode === 'dark'}
      {...brandAssets}
      title={t('auth.login.title')}
      subtitle={t('auth.login.subtitle')}
    >
      <div className="mb-4 flex justify-end">
        <AdminLanguageSwitcher />
      </div>
      <form
        className="space-y-5"
        onSubmit={form.handleSubmit((v) => {
          if (challenge) {
            if (otpCode.length !== 6) {
              toast.error('6 haneli doğrulama kodunu yazın.')
              return
            }
            otpMutation.mutate()
            return
          }
          if (showCaptcha && !recaptchaToken) {
            toast.error(t('auth.login.captchaRequired'))
            return
          }
          mutation.mutate(v)
        })}
        noValidate
      >
        {captchaRequired ? <AuthCaptchaNotice /> : null}

        <div className="space-y-2">
          <label htmlFor="login" className="text-sm font-medium text-foreground">
            {t('auth.login.emailOrUsername')}
          </label>
          <div className="relative">
            <UserRound
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              id="login"
              type="text"
              autoComplete="username"
              placeholder={t('auth.login.emailPlaceholder')}
              className={cn(
                'flex h-11 w-full rounded-xl border border-input bg-background/80 py-2 pl-10 pr-3 text-sm',
                'ring-offset-background transition-shadow placeholder:text-muted-foreground/80',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'dark:bg-mainDark/40 dark:ring-offset-mainDark',
              )}
              {...form.register('login')}
            />
          </div>
          {form.formState.errors.login && (
            <p className="text-sm text-destructive" role="alert">
              {form.formState.errors.login.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium text-foreground">
            {t('auth.login.password')}
          </label>
          <div className="relative">
            <Lock
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className={cn(
                'flex h-11 w-full rounded-xl border border-input bg-background/80 py-2 pl-10 pr-11 text-sm',
                'ring-offset-background transition-shadow placeholder:text-muted-foreground/80',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'dark:bg-mainDark/40 dark:ring-offset-mainDark',
              )}
              {...form.register('password')}
            />
            <button
              type="button"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {form.formState.errors.password && (
            <p className="text-sm text-destructive" role="alert">
              {form.formState.errors.password.message}
            </p>
          )}
        </div>

        {challenge ? (
          <div className="space-y-2">
            <label htmlFor="otp" className="text-sm font-medium text-foreground">
              Doğrulama kodu
            </label>
            <input
              id="otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6 haneli kod"
              className={cn(
                'flex h-11 w-full rounded-xl border border-input bg-background/80 px-3 text-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'dark:bg-mainDark/40 dark:ring-offset-mainDark',
              )}
            />
            <p className="text-sm text-muted-foreground">Telefona gönderilen kodu yazın.</p>
          </div>
        ) : null}

        {showCaptcha && siteKey && !challenge ? (
          <RecaptchaWidget siteKey={siteKey} onChange={setRecaptchaToken} className="flex justify-center" />
        ) : null}

        <button
          type="submit"
          disabled={mutation.isPending || otpMutation.isPending || (!challenge && showCaptcha && !recaptchaToken)}
          className={cn(
            'inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold text-white shadow-glow',
            'bg-mainColor transition-transform hover:bg-mainColor/90 active:scale-[0.98]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mainColor/50 focus-visible:ring-offset-2',
            'disabled:pointer-events-none disabled:opacity-55 dark:bg-darkMainColor dark:hover:bg-darkMainColor/90 dark:focus-visible:ring-darkMainColor/45 dark:ring-offset-mainDark',
          )}
        >
          {mutation.isPending || otpMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {t('auth.login.submitting')}
            </>
          ) : (
            t('auth.login.submit')
          )}
        </button>
      </form>
    </AnimatedLoginShell>
  )
}
